import { useCallback } from 'react';
import Axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { trackError } from '@proposify/frontend-observability';
import { useAppDispatch, useAppSelector } from '../grid/reduxStore/Store';
import { selectContentId } from '../grid/reduxStore/documentSettingsSlice';
import { useSocketClient } from 'providers/SocketContext';
import { ImportPdfIntoDocumentCommand } from '../UndoRedo/ImportPdfIntoDocumentCommand';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion';
import { QueryKeys } from 'utils/queryKeys';
import { fetchPdfPresignedUrl } from '../../../services/api/fetchPdfPresignedUrl';

export const useImportPdfIntoDocument = () => {
  const dispatch = useAppDispatch();
  const { socketClient } = useSocketClient();
  const queryClient = useQueryClient();
  const contentId = useAppSelector(selectContentId);

  return useCallback(
    async ({ file, afterSectionId }: { file: File; afterSectionId?: string }) => {
      try {
        const { putUrl, getUrl, pdfPath } = await fetchPdfPresignedUrl({ fileName: file.name, contentType: file.type });

        await Axios.put(putUrl, file, { headers: { 'Content-Type': file.type } });

        const command = new ImportPdfIntoDocumentCommand(
          { dispatch, socketClient },
          {
            contentId,
            presignedUrl: getUrl,
            filename: file.name,
            afterSectionId,
            pdfPath,
          }
        );

        const addedSectionIds = await command.execute();

        if (addedSectionIds) {
          UndoRedoManagerCommandVersion.getUndoRedoManager().pushUndoRedoCommands(command);
          await queryClient.invalidateQueries({ queryKey: QueryKeys.documentImageLibraryImages(contentId) });
        }

        return addedSectionIds;
      } catch (error) {
        trackError(error instanceof Error ? error : new Error(String(error)), {
          errorFrom: 'editor-remote',
          feature: 'pdf-import',
          contentId,
          filename: file.name,
        });
        throw error;
      }
    },
    [dispatch, socketClient, queryClient, contentId]
  );
};
